import React, { useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  ActivityIndicator,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { theme } from '../../theme/theme';
import { CloseIcon, ImageIcon, PlusIcon } from '../icons';

interface AvatarUploadModalProps {
  visible: boolean;
  onClose: () => void;
  onUploadSuccess: (avatarUrl: string) => void;
  currentAvatar?: string;
}

export const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  visible,
  onClose,
  onUploadSuccess,
  currentAvatar,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const requestPermissions = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('权限不足', '需要访问相册权限才能选择头像');
      return false;
    }
    return true;
  };

  const pickImageFromLibrary = async () => {
    const hasPermission = await requestPermissions();
    if (!hasPermission) return;

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setSelectedImage(result.assets[0].uri);
    }
  };

  const takePhoto = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('权限不足', '需要相机权限才能拍照');
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setSelectedImage(result.assets[0].uri);
    }
  };

  const uploadAvatar = async () => {
    if (!selectedImage) {
      Alert.alert('提示', '请先选择头像图片');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', {
        uri: selectedImage,
        type: 'image/jpeg',
        name: 'avatar.jpg',
      } as any);

      const response = await fetch('http://localhost:3001/users/upload-avatar', {
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          // TODO: Add authorization header
        },
      });

      const result = await response.json();

      if (response.ok) {
        onUploadSuccess(result.data.avatarUrl);
        setSelectedImage(null);
        onClose();
        Alert.alert('成功', '头像上传成功！');
      } else {
        Alert.alert('上传失败', result.message || '头像上传失败');
      }
    } catch (error) {
      console.error('Upload error:', error);
      Alert.alert('上传失败', '网络错误，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedImage(null);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>更换头像</Text>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <CloseIcon size={24} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            {/* 当前头像预览 */}
            <View style={styles.avatarPreview}>
              <Image
                source={{
                  uri: selectedImage || currentAvatar || 'https://via.placeholder.com/120x120/cccccc/666666?text=头像'
                }}
                style={styles.avatarImage}
              />
            </View>

            {/* 选择图片按钮 */}
            <View style={styles.buttonContainer}>
              <TouchableOpacity style={styles.actionButton} onPress={pickImageFromLibrary}>
                <ImageIcon size={24} color={theme.colors.primary} />
                <Text style={styles.buttonText}>从相册选择</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.actionButton} onPress={takePhoto}>
                <PlusIcon size={24} color={theme.colors.primary} />
                <Text style={styles.buttonText}>拍照</Text>
              </TouchableOpacity>
            </View>

            {/* 上传按钮 */}
            {selectedImage && (
              <TouchableOpacity
                style={[styles.uploadButton, uploading && styles.uploadButtonDisabled]}
                onPress={uploadAvatar}
                disabled={uploading}
              >
                {uploading ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text style={styles.uploadButtonText}>上传头像</Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    width: '90%',
    maxWidth: 400,
    padding: 0,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.text,
  },
  closeButton: {
    padding: theme.spacing.xs,
  },
  content: {
    padding: theme.spacing.lg,
  },
  avatarPreview: {
    alignItems: 'center',
    marginBottom: theme.spacing.xl,
  },
  avatarImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: theme.colors.border,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: theme.spacing.xl,
  },
  actionButton: {
    alignItems: 'center',
    padding: theme.spacing.md,
    borderRadius: 12,
    backgroundColor: theme.colors.bg,
    minWidth: 100,
  },
  buttonText: {
    marginTop: theme.spacing.xs,
    fontSize: theme.fontSize.sm,
    color: theme.colors.text,
    textAlign: 'center',
  },
  uploadButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 12,
    padding: theme.spacing.md,
    alignItems: 'center',
  },
  uploadButtonDisabled: {
    opacity: 0.6,
  },
  uploadButtonText: {
    color: 'white',
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
  },
});
